/**
 * offer-copy.ts: what one prepared offer SAYS, computed once for every surface
 * that renders a row (the panel row, the pick line, the shelf row).
 *
 * Three components used to compose the same strings from the same fields,
 * and Offers Engine v2 (docs/OFFERS_ENGINE_V2.md section 6) adds a dozen more
 * rules on top: the "as of" stamps (6.1), the honesty badge and its tooltip
 * (6.2), the deal chip (6.3), information links and the brand-store chip
 * (6.4), the price-kind labels (6.5), attribution and the marketplace note
 * (6.7), the "why this store" sentence (6.8), and pre-orders (R36). One pure
 * function here means a row on the shelf can never say something a row in the
 * panel would not.
 *
 * Pure: no React, no DOM. `now` is injectable so the stamps and the deal chip
 * are deterministic in tests. Every string comes from `UiContent` with the
 * English fallback beside it, the buyActionDescription precedent.
 */

import type { LocaleDetails } from 'shared/src/types/languages.type';
import type { UiContent } from '@/lib/ui-content/ui-content';
import { replacePlaceholders } from '@/lib/ui-content/price-ui-content';
import {
  countOfferRows,
  isAmazonOffer,
  isDealActive,
  isInfoOffer,
  offerObservedAt,
  priceStampFor,
  type PreparedOffer,
} from './offers';
import { formatObservedStamp } from './observed-at';

/** Joins the pieces of a subline. A middle dot, never an em dash. */
export const SUBLINE_SEPARATOR = ' · ';

export type OfferRowCopy = {
  /** The merchant's display name, or its host. */
  name: string;
  /** What the price slot shows: a price ("~" when hedged), "MSRP {price}",
   *  "See price at {merchant}", "Official site", or '' (a search row). */
  priceSlot: string;
  /** True when `priceSlot` is a number to set in tabular figures; false when
   *  it is a label (list price, hidden price, information link). */
  priceSlotIsPrice: boolean;
  /** The stock line: "In stock", "Out of stock", "Pre-order", the search
   *  verb, or the information link's reason. '' when nothing was observed. */
  stockLine: string;
  /** The row's notes: the "as of" stamp, "Member price", "Marketplace
   *  listing", "Deal ends {time}". '' when none apply. */
  notes: string;
  /** `stockLine` and `notes` on one line, for the row's second line. */
  subline: string;
  /** "Our pick" / "Best price", or ''. */
  badgeLabel: string;
  /** "Brand store" when the row earns that chip (6.4.7), else ''. */
  brandStoreLabel: string;
  /** "Deal" while the row's deal is live (6.3), else ''. */
  dealLabel: string;
  /**
   * True when the row carries a deal with an end time, live or lapsed. It
   * decides whether the chip ELEMENT exists, and it reads no clock: a cached
   * page rendered before `endsAt` hydrates after it, so the element must be
   * the same on both sides and only its text (`dealLabel`) may differ. The
   * chip hides itself when that text is empty.
   */
  hasDealChip: boolean;
  /** The honesty badge and its tooltip (6.2), or null. */
  belowUsual: { label: string; tooltip: string } | null;
  /** "around {price}" for a hedged price, else ''. */
  approxSentence: string;
  /** The row's `title` attribute: the "why this store" sentence and the
   *  attribution notice, or undefined when neither applies. */
  title: string | undefined;
  /** The accessible name of the row. */
  ariaLabel: string;
  /** The cached "why this store" sentence (spec 6.8), for an sr-only span
   *  right after the merchant's name. '' when the row has none. Never visible
   *  text, never on the pill, never in JSON-LD. */
  whySentence: string;
  /** Sentences for screen readers and crawlers after the row's visible text,
   *  in order: the usual-price tooltip, the hedge. */
  srOnly: string[];
};

/**
 * `viewerLocal` false (the default: the server render, the hydration pass, a
 * test): times print in the market's zone with the zone named. True (the
 * browser after hydration, `useViewerClock`): in the reader's own zone with
 * "today" / "yesterday" (observed-at.ts). Pure either way.
 */
export function offerRowCopy(
  offer: PreparedOffer,
  ui: UiContent,
  localeDetails: LocaleDetails,
  now: number = Date.now(),
  viewerLocal = false,
): OfferRowCopy {
  const isSearch = offer.kind === 'search';
  const isInfo = isInfoOffer(offer);
  const name = offer.merchantName?.trim() || offer.domainKey;
  const priceKind = offer.priceKind;
  const searchNow = ui.searchNow ?? 'Search';
  const officialSite = ui.officialSite ?? 'Official site';

  // Low-confidence prices are hedged, never asserted: "~$129.99" visually,
  // with the full localized sentence carried for screen readers and AI.
  const approxSentence =
    offer.isApproximate && offer.priceLabel && !isInfo && !isSearch
      ? replacePlaceholders(ui.approxPrice ?? 'around {price}', { price: offer.priceLabel })
      : '';

  // The price slot (spec 6.4.6, 6.5).
  let priceSlot = '';
  let priceSlotIsPrice = false;
  if (isSearch) {
    priceSlot = '';
  } else if (isInfo) {
    priceSlot = officialSite;
  } else if (priceKind === 'hidden') {
    priceSlot = replacePlaceholders(ui.seePriceLabel ?? 'See price at {merchant}', { merchant: name });
  } else if (priceKind === 'msrp') {
    // A list price is still a number we read, so a hedged one keeps its "~"
    // (spec 6.5: isApproximate applies to every kind that shows a number).
    priceSlot = offer.priceLabel
      ? replacePlaceholders(ui.msrpLabel ?? 'MSRP {price}', {
          price: offer.isApproximate ? `~${offer.priceLabel}` : offer.priceLabel,
        })
      : '';
  } else {
    priceSlot = offer.isApproximate && offer.priceLabel ? `~${offer.priceLabel}` : offer.priceLabel;
    priceSlotIsPrice = Boolean(offer.priceLabel);
  }

  // The stock line. A SEARCH row says what it is; an information link says why
  // it is one; a priced row reports the stock state we actually observed.
  // Anything else says nothing at all: printing "In stock" for a row whose
  // availability is missing would be a claim about a merchant's warehouse made
  // on no evidence.
  const stockLine = isSearch
    ? searchNow
    : isInfo
      ? offer.infoReason === 'no-direct-sales'
        ? replacePlaceholders(ui.noDirectSales ?? 'Does not sell online in {region}', {
            region: localeDetails.region,
          })
        : (ui.seeProductPage ?? "Product details on the brand's site")
      : offer.availability === 'out_of_stock'
        ? (ui.outOfStock ?? 'Out of stock')
        : offer.availability === 'preorder'
          ? (ui.preorderLabel ?? 'Pre-order')
          : offer.availability === 'in_stock'
            ? (ui.inStock ?? 'In stock')
            : '';

  // The notes: the "as of" stamp (6.1), the price-kind and marketplace
  // sublines (6.5, 6.7), the deal's end (6.3). Until the browser can say the
  // reader's own zone (`viewerLocal`), times are printed in the market's zone,
  // zone named, so the server and the hydration pass agree.
  const notes: string[] = [];
  if (!isSearch && !isInfo) {
    const stamp = priceStampFor(offer, now);
    if (stamp) {
      const time = formatObservedStamp(
        offerObservedAt(offer),
        localeDetails.path,
        localeDetails.regionCode,
        viewerLocal,
        now,
      );
      if (time) {
        notes.push(
          replacePlaceholders(
            stamp === 'amazon'
              ? (ui.amazonPriceAsOf ?? 'Amazon price · {time}')
              : (ui.priceAsOf ?? 'Price seen · {time}'),
            { time },
          ),
        );
      }
    }
    if (priceKind === 'member') notes.push(ui.memberPriceLabel ?? 'Member price');
    if (offer.merchantKind === 'marketplace') notes.push(ui.marketplaceListing ?? 'Marketplace listing');
    if (isDealActive(offer, now)) {
      const ends = formatObservedStamp(
        offer.deal?.endsAt,
        localeDetails.path,
        localeDetails.regionCode,
        viewerLocal,
        now,
      );
      if (ends) notes.push(replacePlaceholders(ui.dealEnds ?? 'Deal ends {time}', { time: ends }));
    }
  }
  const notesText = notes.join(SUBLINE_SEPARATOR);
  const subline = [stockLine, notesText].filter(Boolean).join(SUBLINE_SEPARATOR);

  const badgeLabel =
    offer.badge === 'bestPlace'
      ? (ui.bestPlaceBadge ?? 'Our pick')
      : offer.badge === 'bestPrice'
        ? (ui.bestPriceBadge ?? 'Best price')
        : '';
  // A priced brand row wears "Brand store" only when it carries no other
  // badge (6.4.7); an information link is not a store.
  const brandStoreLabel =
    offer.merchantKind === 'brand' && !badgeLabel && !isInfo && !isSearch && offer.priceLabel
      ? (ui.brandStoreBadge ?? 'Brand store')
      : '';
  const hasDealChip = !isInfo && !isSearch && Number.isFinite(Date.parse(offer.deal?.endsAt ?? ''));
  const dealLabel = hasDealChip && isDealActive(offer, now) ? (ui.dealBadge ?? 'Deal') : '';

  const belowUsual =
    typeof offer.belowUsualPercent === 'number'
      ? {
          label: replacePlaceholders(ui.belowUsualBadge ?? '{percent}% below its usual price', {
            percent: String(offer.belowUsualPercent),
          }),
          tooltip: replacePlaceholders(
            ui.usualPriceTooltip ??
              'Usual price {price}: the median of the daily prices we observed at this store over the last 60 days, {days} days of data',
            {
              price: offer.typicalPriceLabel,
              days: String(offer.typicalPricePoints ?? ''),
            },
          ),
        }
      : null;

  // The "why this store" sentence is a tooltip and an sr-only sentence, never
  // visible text (6.8); the attribution notice rides the same tooltip (6.7).
  const titleParts = [
    offer.why?.trim() || '',
    offer.attributionRequired
      ? replacePlaceholders(ui.dataProvidedBy ?? 'Data provided by {merchant}', { merchant: name })
      : '',
  ].filter(Boolean);
  const title = titleParts.length > 0 ? titleParts.join(SUBLINE_SEPARATOR) : undefined;

  // A search row is labelled by what it does, an information link by what it
  // is, a priced row by its price. A row with none of these (a partner link on
  // a card that has no price yet) is just the merchant.
  const ariaLabel = isSearch
    ? `${searchNow}: ${name}`
    : isInfo
      ? `${officialSite}: ${name}`
      : approxSentence || priceSlot
        ? `${name}: ${approxSentence || priceSlot}`
        : name;

  const whySentence = offer.why?.trim() || '';
  const srOnly = [belowUsual?.tooltip ?? '', approxSentence].filter(Boolean);

  return {
    name,
    priceSlot,
    priceSlotIsPrice,
    stockLine,
    notes: notesText,
    subline,
    badgeLabel,
    brandStoreLabel,
    dealLabel,
    hasDealChip,
    belowUsual,
    approxSentence,
    title,
    ariaLabel,
    whySentence,
    srOnly,
  };
}

/** True when the panel lists an Amazon PRICE (a search row is not one), which
 *  is when Amazon's disclaimer must be present (spec 6.1.4). */
export const panelHasAmazonRow = (rows: ReadonlyArray<PreparedOffer>): boolean =>
  rows.some((row) => isAmazonOffer(row) && row.kind !== 'search' && !isInfoOffer(row));

/** Amazon's disclaimer, the English fallback for locales without a hand-set string. */
export const AMAZON_PRICE_DISCLAIMER_FALLBACK =
  'Product prices and availability are accurate as of the date and time indicated and are subject to change. Any price and availability information displayed on Amazon at the time of purchase will apply to the purchase of this product.';

/**
 * The panel's note on its prices (in the info icon: offersDisclosureNotes). It is counted from the same rows, with the
 * same `countOfferRows`, as the pill's visible "{n} stores" label, so the two
 * can never disagree: the pill used to say "2 stores" (Oticon plus an Amazon
 * search link) while this sentence said "we track one retailer, Oticon".
 * The single-store sentence names the one row a reader can buy from.
 */
export function offersPanelIntro({
  rows,
  primaryDomainKey,
  productTitle,
  region,
  ui,
}: {
  rows: ReadonlyArray<PreparedOffer>;
  primaryDomainKey?: string;
  productTitle: string;
  region: string;
  ui: Pick<UiContent, 'offersPanelIntro' | 'offersPanelIntroSingleStore'>;
}): string {
  const buyable = rows.filter((row) => !isInfoOffer(row));
  const seller =
    buyable.find((row) => row.domainKey === primaryDomainKey) ?? buyable[0] ?? rows[0];
  const sellerName = seller?.merchantName || seller?.domainKey || '';
  const template =
    countOfferRows(rows) <= 1
      ? (ui.offersPanelIntroSingleStore ??
        'Where to buy {product} in {region}: we track one retailer, {seller}, and show the price we last observed there; the price you see at checkout may differ.')
      : (ui.offersPanelIntro ??
        'Where to buy {product} in {region}: the trusted retailers we track in this market, with the best price we have observed at each. Prices are checked regularly and can change, and the retailer confirms the final price at checkout.');
  return replacePlaceholders(template, { product: productTitle ?? '', region, seller: sellerName });
}

/**
 * The notices a store list's info icon carries (OffersDisclosure, the shelf
 * dock): what its prices are (offersPanelIntro: the best price we observed,
 * the store confirms at checkout) and, when it lists an Amazon price,
 * Amazon's price disclaimer, which its licence lets sit behind "a hyperlink,
 * pop-up box ... or other similar method" (Associates IP License 2(c)(i)).
 * Until 2026-10-03 both were also sr-only paragraphs: machine-only text a
 * sighted reader never saw.
 */
export function offersDisclosureNotes(input: Parameters<typeof offersPanelIntro>[0] & {
  ui: Pick<UiContent, 'offersPanelIntro' | 'offersPanelIntroSingleStore' | 'amazonPriceDisclaimer'>;
}): string[] {
  const notes = [offersPanelIntro(input)];
  if (panelHasAmazonRow(input.rows)) {
    notes.push(input.ui.amazonPriceDisclaimer ?? AMAZON_PRICE_DISCLAIMER_FALLBACK);
  }
  return notes;
}
